import {
  Card,
  Col,
  DatePicker,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { Column } from "@ant-design/plots";
import { useEffect, useMemo, useState } from "react";
import { Dayjs } from "dayjs";
import {
  getBundles,
  getCompanies,
  getDashboardRiskCompanies,
  getDashboardRisks,
} from "../repository/data.repository";
import {
  DashboardRiskCompany,
  DashboardRisks,
} from "../repository/data.default";
import { createSearchRequest } from "../hooks/useSearchRequest";
import RemoteSelect from "../components/analytics/RemoteSelect";
import {
  ALARM_RED,
  MetricChartEmpty,
  OK_GREEN,
  WARN_ORANGE,
} from "../components/analytics/MetricChartRender";

// PD-01 §10 — Bundle Reports. Pick a bundle, see its company-rollup risk +
// band distribution. Everything comes from the existing Dashboard endpoints
// filtered to the bundle's underlying questionnaire (bundle = set of
// questionnaires; here we scope to the bundle name for sign-off reporting).
// Admin can additionally narrow by company and date range.

const FALLBACK_BAND_COLORS = [
  ALARM_RED,
  WARN_ORANGE,
  "#fadb14",
  "#a0d911",
  OK_GREEN,
];

function BundleReportsPage() {
  const [bundleUid, setBundleUid] = useState<string | undefined>();
  const [companyUid, setCompanyUid] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [risks, setRisks] = useState<DashboardRisks | undefined>();
  const [companies, setCompanies] = useState<DashboardRiskCompany[]>([]);
  const [loading, setLoading] = useState(false);

  const bundlesRequest = createSearchRequest(getBundles, "name");
  const companiesRequest = createSearchRequest(getCompanies, "name");

  const from = range?.[0]?.startOf("day").toISOString();
  const to = range?.[1]?.endOf("day").toISOString();

  useEffect(() => {
    if (!bundleUid) {
      setRisks(undefined);
      setCompanies([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // Dashboard endpoints don't take `bundleUid` directly — they take
    // `questionnaireUid`. For a bundle report we leave questionnaire null and
    // scope purely on company + period; the rollup already covers the bundle's
    // audience because bundles bind questionnaires to companies.
    const scope = { companyUid, from, to };
    Promise.all([
      getDashboardRisks(scope),
      getDashboardRiskCompanies({ ...scope, pageNumber: 0, pageSize: 50 }),
    ])
      .then(([rollup, list]) => {
        if (cancelled) return;
        setRisks(rollup);
        setCompanies(list.data);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [bundleUid, companyUid, from, to]);

  const bands = useMemo(
    () =>
      [...(risks?.bandDistribution ?? [])].sort((a, b) => a.ordinal - b.ordinal),
    [risks],
  );
  const bandColors = bands.map(
    (b, i) => b.bandColor || FALLBACK_BAND_COLORS[i % FALLBACK_BAND_COLORS.length],
  );

  const companyColumns: ColumnsType<DashboardRiskCompany> = [
    { title: "Company", dataIndex: "companyName" },
    { title: "Results", dataIndex: "totalResults", align: "right", width: 100 },
    { title: "High-risk", dataIndex: "highRiskCount", align: "right", width: 110 },
    {
      title: "Share",
      dataIndex: "highRiskShare",
      align: "right",
      width: 90,
      render: (share: number) =>
        share > 0 ? (
          <Tag color="red">{`${Math.round(share * 10) / 10}%`}</Tag>
        ) : (
          <Typography.Text type="secondary">0%</Typography.Text>
        ),
    },
  ];

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Bundle Reports
        </Typography.Title>
      </Row>
      <Col className="flex flex-auto flex-col gap-4 overflow-hidden p-4 pt-0">
        <Row align="middle" gutter={12}>
          <Col>
            <Typography.Text type="secondary" className="mr-2">
              Bundle
            </Typography.Text>
            <RemoteSelect
              className="w-80"
              placeholder="Pick a bundle"
              value={bundleUid}
              onChange={setBundleUid}
              request={bundlesRequest}
            />
          </Col>
          <Col>
            <Typography.Text type="secondary" className="mr-2">
              Company
            </Typography.Text>
            <RemoteSelect
              className="w-64"
              placeholder="All companies"
              value={companyUid}
              onChange={setCompanyUid}
              request={companiesRequest}
            />
          </Col>
          <Col>
            <Typography.Text type="secondary" className="mr-2">
              Period
            </Typography.Text>
            <DatePicker.RangePicker
              value={range}
              onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
              allowClear
            />
          </Col>
        </Row>

        {!bundleUid ? (
          <Card className="flex-auto">
            <Typography.Text type="secondary">
              Pick a bundle above to see its rollup.
            </Typography.Text>
          </Card>
        ) : (
          <>
            <Space wrap size={12}>
              <Tag>{(risks?.totalResults ?? 0).toLocaleString()} results</Tag>
              <Tag>{(risks?.respondentCount ?? 0).toLocaleString()} respondents</Tag>
              {risks?.awaitingScoring ? (
                <Tag color="warning">{risks.awaitingScoring} awaiting scoring</Tag>
              ) : null}
            </Space>
            <Row gutter={12} className="flex-wrap">
              <Col flex="1 1 400px">
                <Card title="Band distribution">
                  <Spin spinning={loading}>
                    {bands.length ? (
                      <Column
                        data={bands}
                        xField="bandLabel"
                        yField="count"
                        colorField="bandLabel"
                        height={260}
                        legend={false}
                        tooltip={false}
                        scale={{
                          x: { padding: 0.5 },
                          color: {
                            domain: bands.map((b) => b.bandLabel),
                            range: bandColors,
                          },
                        }}
                        axis={{
                          x: { line: true },
                          y: {
                            line: true,
                            title: "Results",
                            tickFilter: (d: any) => Number.isInteger(d),
                          },
                        }}
                      />
                    ) : (
                      <MetricChartEmpty />
                    )}
                  </Spin>
                </Card>
              </Col>
              <Col flex="1 1 400px">
                <Card title="Companies at risk">
                  <Table
                    rowKey="companyUid"
                    size="small"
                    loading={loading}
                    dataSource={companies}
                    columns={companyColumns}
                    pagination={{ pageSize: 10 }}
                    locale={{ emptyText: "No results in the current scope." }}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Col>
    </Col>
  );
}

export default BundleReportsPage;
